import { useContext, useState } from "react";
import {
  KursusFiltersContext,
  KursusFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  KursusFilterKeys,
  KursusStartDatoSetting,
} from "../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../select/Select";
import { DatePicker as RSDatePicker } from "rsuite";
import { format } from "date-fns";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { ComboBox } from "../../../filter/ComboBox";
import { KursusholdFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { dateQueries } from "../../../../config/filter/CustomFilterConfig";
import { isCompleteDate } from "../../../formcontrols/datePickerValue";

// helper
const filterKey: QueryFilter<KursusFilterKeys>["filterKey"] = "startdato";

export default function KursusStartDatoFilter() {
  // query filter context
  const filters = useContext(KursusFiltersContext);
  const updateFilters = useContext(KursusFiltersEventContext);

  // find the filter settings
  const settings: KursusStartDatoSetting[] = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // things
  const queries = dateQueries;

  // state things
  const [selectedQuery, setSelectedQuery] = useState(queries[0]);
  // null while the typed date is unfinished (ADV-320)
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  // handler
  function updateFilterHandler() {
    if (!selectedDate) return;
    const dateInput = format(selectedDate, "yyyy-MM-dd");

    // update filter settings
    const id: KursusStartDatoSetting["id"] = `${selectedQuery.query}-${dateInput}`;

    if (settings?.some((set) => set.id === id)) {
      alert("filter findes allerede");
      return;
    }

    const settingsItem: KursusStartDatoSetting = {
      id: id,
      query: selectedQuery,
      value: dateInput,
    };

    const updatedSettings: KursusStartDatoSetting[] = settings || [];
    updatedSettings.push(settingsItem);

    if (updatedSettings.length === 0) {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
      return;
    }

    const filterString = f<KursusholdFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => {
          return {
            fraDato: {
              [set.query.query]: set.value,
            },
          };
        }),
      )
      .build();

    const queryFilter: QueryFilter<KursusFilterKeys, KursusStartDatoSetting[]> =
      {
        filter: filterString,
        filterKey: filterKey,
        settings: updatedSettings,
        parentFilter: "and",
      };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterEventHandler(id: string) {
    const filteredSettings = settings?.filter((set) => set.id !== id);

    if (filteredSettings?.length === 0) {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
      return;
    }

    const filterString = f<KursusholdFilterInput>()
      .addFilter(
        "or",
        filteredSettings?.map((set) => {
          return {
            fraDato: {
              [set.query.query]: set.value,
            },
          };
        }),
      )
      .build();

    const queryFilter: QueryFilter<KursusFilterKeys, KursusStartDatoSetting[]> =
      {
        filter: filterString,
        filterKey: filterKey,
        settings: filteredSettings,
        parentFilter: "and",
      };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  return (
    <AccordionFilterItem
      title="Startdato"
      value="kursus-startdato"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings.map((set) => (
            <AppliedFilterItem
              labelPrefix={`${set.query.displayName}`}
              label={`${format(new Date(set.value), "dd-MM-yyyy")}`}
              removeFilterHandler={() => {
                const id: KursusStartDatoSetting["id"] = `${set.query.query}-${set.value}`;
                removeFilterEventHandler(id);
              }}
              key={`${set.query.query}-${set.value}`}
            />
          ))}
        </div>
      )}

      <ComboBox>
        <Select
          defaultValue={selectedQuery?.query}
          onValueChange={(value: string) => {
            const query = queries.find((q) => q.query === value);
            if (query) {
              setSelectedQuery(query);
            }
          }}
        >
          <SelectTrigger placeholder="Vælg query type" badge />
          <SelectContent small>
            {queries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <RSDatePicker
          appearance="subtle"
          cleanable={false}
          format="dd.MM.yyyy"
          oneTap
          size="xs"
          onChange={(date) => {
            setSelectedDate(isCompleteDate(date) ? date : null);
          }}
        />
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 flex-1`}
          disabled={!selectedDate}
          onClick={() => {
            updateFilterHandler();
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
